import { inject, injectable } from '@wix/typesafe-inversify';
import type { SerializedBreakpointsData } from '@wix/document-services-types';
import type { ComponentRef, EditorSDK } from '@wix/editor-platform-sdk-types';
import {
  createPopupContainer,
  createResponsivePopupContainer,
} from '../../utils/createPopupContainer';
import { createResponsiveSection } from '../../utils/createResponsiveSection';
import type { IEditorTransactions } from '../EditorTransactions/IEditorTransactions';
import type { IPagesService } from './IPagesService';
import { Services } from '../../ioc/services';
import type { IExperiments } from '../Experiments/IExperiments';

const token = 'token';
const COMPONENTS_SECTION = 'wysiwyg.viewer.components.ClassicSection';
const COMPONENTS_SECTION_RESPONSIVE = 'responsive.components.Section';

const breakpoints: SerializedBreakpointsData = {
  type: 'BreakpointsData',
  values: [
    {
      type: 'BreakpointRange',
      id: 'universal-editor-app-tablet-breakpoint',
      min: 320,
      max: 1000,
    },
    {
      type: 'BreakpointRange',
      id: 'universal-editor-app-mobile-breakpoint',
      min: 320,
      max: 750,
    },
  ],
};

@injectable()
export class PagesService implements IPagesService {
  constructor(
    @inject(Services.EditorSDK)
    private editorSDK: EditorSDK,
    @inject(Services.EditorTransactions)
    private editorTransactions: IEditorTransactions,
    @inject(Services.Experiments)
    private experiments: IExperiments,
  ) {}

  public addPage: IPagesService['addPage'] = async ({
    title,
    isResponsive,
    appDefinitionId,
    pageId,
  }) => {
    const pageUriSEO =
      await this.editorSDK.pages.pageUriSEO.convertPageNameToUrl(token, {
        title,
      });

    const pageRef = await this.editorTransactions.runAndWaitForApproval(() =>
      this.editorSDK.pages.add(token, {
        title,
        definition: {
          type: 'Page',
          components: isResponsive ? [createResponsiveSection()] : [],
          data: {
            pageUriSEO,
            managingAppDefId: appDefinitionId,
            tpaPageId: pageId,
            appDefinitionId,
          },
          breakpoints: isResponsive ? breakpoints : undefined,
          componentType: 'mobile.core.components.Page',
        },
        shouldAddMenuItem: true,
      }),
    );

    return pageRef as ComponentRef;
  };

  public findSectionInPage: IPagesService['findSectionInPage'] = async (
    pageRef: ComponentRef,
    isResponsive: boolean,
  ) => {
    const pageChildren = await this.editorSDK.components.getChildren(token, {
      componentRef: pageRef,
    });
    const types = await Promise.all(
      pageChildren.map((componentRef) =>
        this.editorSDK.components.getType(token, {
          componentRef,
        }),
      ),
    );
    const sectionType = isResponsive
      ? COMPONENTS_SECTION_RESPONSIVE
      : COMPONENTS_SECTION;
    const sectionIndex = types.findIndex((type) => type === sectionType);

    return pageChildren[sectionIndex];
  };

  public createLightbox: IPagesService['createLightbox'] = async ({
    title,
    appDefinitionId,
    lightboxId,
    isResponsive,
    size,
  }) => {
    const popupRef = await this.editorTransactions.runAndWaitForApproval(
      async () => {
        const pageRef = await this.editorSDK.pages.popupPages.add(token, {
          title,
          definition: {
            data: {
              managingAppDefId: appDefinitionId,
              tpaPageId: lightboxId,
              // Wrong typings in editor platform, `indexable: true` is needed for the lightbox to appear in editor menu
              indexable: true as false,
              isPopup: true,
              appDefinitionId,
            },
            breakpoints,
            ...(isResponsive
              ? {
                  componentType: 'mobile.core.components.Page',
                  components: [createResponsivePopupContainer()],
                }
              : {
                  components: [createPopupContainer(size)],
                }),
          },
        });

        await this.editorSDK.pages.popupPages.updateBehavior(token, {
          popupRef: pageRef,
          behavior: {
            name: 'openPopup',
            params: {
              openInDesktop: false,
              openInMobile: false,
              delay: 0,
            },
          },
        });

        if (isResponsive) {
          await this.editorSDK.pages.popupPages.open(token, {
            popupRef: pageRef,
          });
        }

        return pageRef;
      },
    );

    return popupRef as ComponentRef;
  };
}
